import { rd } from '@passionware/monads';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import Account from 'lib/auth/Account';
import { getGroups } from 'store/v1/groups/groups.actions.js';
import { getRates } from 'store/v1/rates/rates.actions.js';
import { selectActiveAccount } from 'store/accounts';
import { selectActiveTeam } from 'store/teams';
import ModalHandler from 'v1/components/modals/ModalHandler';
import { Helmet } from 'react-helmet';
import ReactTooltip from 'react-tooltip';
import moment from 'moment-timezone';
import { get } from 'lodash';
import { createSocketClient, SocketContext } from 'lib/websockets';
import './App.scss';
import { websocketStore } from '../../v5/api/external-event/external-event-api.connected';
import { ModalContainerV5 } from '../../v5/features/core/ModalContainerV5.js';
import { CommandBarWidgetConnected } from '../../v5/features/commandbar/CommandBarWidget.connected';
import { TooltipProvider } from '../../v5/design-system/Tooltip.js';
import ModalProvider from '../../modals/ModalProvider.js';
import { setSystemTimezone } from '~/v5/core/system-timezone.js';
import { Toaster } from 'v5/design-system/sonner.js';
import { mySupportService } from 'v5/services/front/SupportService/SupportService.connected';
import { VersionUpdateNotifierWidget } from 'v5/features/core/version-update-notifier/VersionUpdateNotifierWidget';
import { useBrowsingDefaultTimezone } from 'v5/services/internal/SystemSettingsService/legacyDefaultTimezone.connected';

document.addEventListener('wheel', function (event) {
  if (document.activeElement.type === 'number') {
    document.activeElement.blur();
  }
});

const toastOptions = {
  richColors: true,
  offset: { left: '15rem' },
  className: 'tailwind-root',
  toastOptions: {
    classNames: {
      toast: '[&_[data-icon]_svg]:size-5'
    }
  },
  visibleToasts: 4,
  expand: true
};

function useSystemTimezoneSync() {
  const loggedIn = useSelector(state => Boolean(get(state, 'auth.loggedIn')));
  const systemTzForLoggedInUsers = rd.tryGet(
    useBrowsingDefaultTimezone(loggedIn)
  )?.value;

  const systemTzForPublicPages = useSelector(state =>
    get(state, 'public_settings.settings.defaultsTimezone')
  );

  const effectiveSystemTz = systemTzForLoggedInUsers || systemTzForPublicPages;

  useEffect(() => {
    if (effectiveSystemTz) {
      // Set the default timezone for moment.js
      moment.tz.setDefault(effectiveSystemTz);
      // Set the system timezone in the v5 core
      setSystemTimezone(effectiveSystemTz);
    }
  }, [effectiveSystemTz]);
}

export function App({ children }) {
  const dispatch = useDispatch();
  const auth = useSelector(state => state.auth);
  const team = useSelector(state => selectActiveTeam(state));
  const account = useSelector(state => selectActiveAccount(state));
  const { loggedIn } = auth;

  useSystemTimezoneSync();

  const [socket, setSocket] = useState(undefined);

  function loadSystemData() {
    dispatch(getGroups());
    dispatch(getRates());
  }

  useEffect(() => {
    if (loggedIn && account) {
      loadSystemData();

      if (!socket) {
        const socket = createSocketClient();
        setSocket(socket);
        // inject into v5 world
        websocketStore.setNewValue(socket);
      }

      if (account) {
        mySupportService.boot({
          email: get(account, 'email'),
          name: `${get(account, 'firstName', '')} ${get(account, 'lastName', '')}`,
          userId: get(account, 'id')
        });
      }
    } else {
      if (socket) {
        socket.close();
        setSocket(null);
        websocketStore.setNewValue(null);
      }
    }
  }, [loggedIn, account]);

  return (
    <div className="App">
      <TooltipProvider>
        <Helmet>
          <title>{`Atellio${
            Account.tenantName() ? ` | ${Account.tenantName()}` : ''
          } ${team?.name ? ` | ${team.name}` : ''}`}</title>
          <meta
            name="viewport"
            content="width=device-width, user-scalable=no, initial-scale=1, maximum-scale=1"
          />
          <meta
            name="keywords"
            content="Talent, TMS, CMS, Talent Management, Freelancer, Developer, Creative, Contractor, Freelance, Art, Artist, Illustration, Illustrator, Hire Illustrator, Promotion, Editorial, Advertising, Creative, Design, Agent, Agency, Collective, Commission, Promotion, Portfolio, Animation, Motion, Hand Lettering, Licensing"
          />
          <meta
            name="description"
            content="Atellio is reinventing the production process for brands & agencies, by organizing & automating the production workflow."
          />
          <meta property="og:image" content="/images/meta_image.jpg" />
          <meta property="og:type" content="website" />
        </Helmet>
        <SocketContext.Provider value={socket}>
          <ModalProvider>{children}</ModalProvider>
        </SocketContext.Provider>
        <ReactTooltip place="bottom" effect="solid" delayShow={300} />
        <ModalHandler />
        <ModalContainerV5 />
        <CommandBarWidgetConnected />
        <VersionUpdateNotifierWidget />
        <Toaster {...toastOptions} />
      </TooltipProvider>
    </div>
  );
}

export default App;
